import React, { useEffect, useRef } from 'react';

const BrandsPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const brandFeatures = [
    {
      title: "Trusted Quality",
      icon: "fas fa-shield-halved",
      description: "Living up to the 'Unfailing' promise with consistent quality and reliability"
    },
    {
      title: "Versatile Designs",
      icon: "fas fa-palette",
      description: "Extensive range of designs and varieties to suit every cooking need"
    },
    {
      title: "Professional Grade",
      icon: "fas fa-star",
      description: "Suitable for both home cooks and professional kitchens"
    },
    {
      title: "Continuous Innovation",
      icon: "fas fa-lightbulb",
      description: "Constantly evolving with new designs and colors for enhanced cooking experience"
    }
  ];

  const applicationAreas = [
    {
      title: "Household Kitchens",
      icon: "fas fa-home",
      description: "Perfect for everyday home cooking and family meals",
      features: ["Family-sized portions", "Easy maintenance", "Durable construction", "Aesthetic appeal"]
    },
    {
      title: "Restaurants",
      icon: "fas fa-utensils",
      description: "Professional-grade utensils for commercial kitchen operations",
      features: ["High-volume cooking", "Commercial durability", "Efficient heat distribution", "Professional finish"]
    },
    {
      title: "Hotels",
      icon: "fas fa-building",
      description: "Premium quality for hospitality industry standards",
      features: ["Elegant presentation", "Consistent performance", "Large-scale cooking", "Guest satisfaction"]
    },
    {
      title: "Events & Catering",
      icon: "fas fa-glass-cheers",
      description: "Reliable solutions for weddings, gatherings, and special occasions",
      features: ["Event-ready designs", "Bulk cooking capacity", "Portable solutions", "Impressive presentation"]
    }
  ];

  const productCategories = [
    {
      title: "Traditional Cookware",
      items: ["Kadhai", "Handis", "Biryani Pots", "Topes"],
      description: "Authentic designs for traditional cooking methods"
    },
    {
      title: "Modern Kitchen",
      items: ["Frying Pans", "Sauce Pans", "Stock Pots", "Skillets"],
      description: "Contemporary designs for modern cooking styles"
    },
    {
      title: "Specialty Items",
      items: ["Idli Makers", "Pressure Cookers", "Steamers", "Roasters"],
      description: "Specialized utensils for specific cooking techniques"
    },
    {
      title: "Serving Solutions",
      items: ["Serving Bowls", "Platters", "Ladles", "Serving Spoons"],
      description: "Complete serving and presentation solutions"
    }
  ];

  return (
    <div className="brands-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .brands-page {
          font-family: 'Poppins', sans-serif;
          line-height: 1.6;
          color: var(--graphite, #1E2329);
          overflow-x: hidden;
        }

        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          color: var(--slate-grey, #4A5260);
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: var(--slate-grey, #4A5260);
          max-width: 600px;
          margin: 0 auto;
        }

        .brand-showcase-section {
          padding: 100px 0;
          background: var(--off-white, #F6F7F9);
        }

        .brand-showcase {
          background: white;
          border-radius: 20px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          margin-bottom: 3rem;
        }

        .brand-header {
          background: linear-gradient(135deg, var(--brand-purple, #7B3FE4) 0%, var(--deep-plum, #5A1FB3) 100%);
          padding: 4rem 2rem;
          text-align: center;
          color: white;
          position: relative;
        }

        .brand-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="white" opacity="0.1"><rect x="10" y="10" width="20" height="20" rx="3"/><circle cx="70" cy="25" r="8"/></svg>');
          background-repeat: repeat;
        }

        .brand-logo-placeholder {
          width: 120px;
          height: 120px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          position: relative;
          z-index: 2;
          backdrop-filter: blur(10px);
        }

        .brand-logo-placeholder i {
          font-size: 3rem;
          color: white;
          opacity: 0.8;
        }

        .brand-name {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 1rem;
          position: relative;
          z-index: 2;
        }

        .brand-tagline {
          font-size: 1.3rem;
          opacity: 0.95;
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 2;
        }

        .brand-meaning {
          font-size: 1.1rem;
          opacity: 0.9;
          font-style: italic;
          position: relative;
          z-index: 2;
        }

        .brand-content {
          padding: 3rem;
        }

        .brand-story {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 3rem;
          align-items: center;
          margin-bottom: 3rem;
        }

        .brand-description {
          flex: 1;
        }

        .brand-description h3 {
          font-size: 2rem;
          font-weight: 700;
          color: var(--slate-grey, #4A5260);
          margin-bottom: 1.5rem;
        }

        .brand-description p {
          font-size: 1.1rem;
          color: var(--slate-grey, #4A5260);
          line-height: 1.8;
          margin-bottom: 1.5rem;
        }

        .brand-highlights {
          list-style: none;
        }

        .brand-highlights li {
          padding: 0.5rem 0;
          color: var(--slate-grey, #4A5260);
          position: relative;
          padding-left: 1.5rem;
          font-weight: 500;
        }

        .brand-highlights li::before {
          content: '●';
          position: absolute;
          left: 0;
          color: var(--brand-purple, #7B3FE4);
          font-weight: bold;
        }

        .brand-image-placeholder {
          background: var(--off-white, #F6F7F9);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .brand-image {
          width: 100%;
          height: 300px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 3px solid var(--brand-purple, #7B3FE4);
          position: relative;
        }

        .brand-image::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="%231E88E5" opacity="0.05"><rect x="10" y="10" width="20" height="20" rx="3"/></svg>');
          background-repeat: repeat;
        }

        .image-content {
          text-align: center;
          color: var(--brand-purple, #7B3FE4);
          position: relative;
          z-index: 2;
        }

        .image-content i {
          font-size: 4rem;
          margin-bottom: 1rem;
          display: block;
          opacity: 0.7;
        }

        .image-text {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .image-subtitle {
          font-size: 1rem;
          opacity: 0.8;
        }

        .image-caption {
          padding: 1rem;
          text-align: center;
        }

        .image-caption h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--slate-grey, #4A5260);
          margin-bottom: 0.3rem;
        }

        .image-caption p {
          color: var(--slate-grey, #4A5260);
          font-size: 0.9rem;
        }

        .features-section {
          padding: 100px 0;
          background: white;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .feature-card {
          background: var(--off-white, #F6F7F9);
          padding: 2.5rem;
          border-radius: 16px;
          text-align: center;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .feature-card:hover {
          border-color: var(--brand-purple, #7B3FE4);
          transform: translateY(-5px);
        }

        .feature-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, var(--brand-purple, #7B3FE4), var(--deep-plum, #5A1FB3));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .feature-icon i {
          font-size: 1.8rem;
          color: white;
        }

        .feature-card h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--slate-grey, #4A5260);
          margin-bottom: 1rem;
        }

        .feature-card p {
          color: var(--slate-grey, #4A5260);
          line-height: 1.6;
        }

        .applications-section {
          padding: 100px 0;
          background: var(--off-white, #F6F7F9);
        }

        .applications-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2rem;
        }

        .application-card {
          background: white;
          padding: 2.5rem;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .application-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
        }

        .app-header {
          display: flex;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .app-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, var(--brand-purple, #7B3FE4), var(--deep-plum, #5A1FB3));
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.5rem;
        }

        .app-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .app-header h3 {
          font-size: 1.4rem;
          font-weight: 600;
          color: var(--slate-grey, #4A5260);
        }

        .app-description {
          color: var(--slate-grey, #4A5260);
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }

        .app-features {
          list-style: none;
        }

        .app-features li {
          padding: 0.4rem 0;
          color: var(--slate-grey, #4A5260);
          position: relative;
          padding-left: 1.2rem;
          font-size: 0.95rem;
        }

        .app-features li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: var(--brand-purple, #7B3FE4);
          font-weight: bold;
        }

        .product-gallery-section {
          padding: 100px 0;
          background: white;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .gallery-item {
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          transition: all 0.4s ease;
        }

        .gallery-item:hover {
          transform: translateY(-8px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
        }

        .gallery-image {
          width: 100%;
          height: 250px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 3px solid var(--brand-purple, #7B3FE4);
          position: relative;
        }

        .gallery-image::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="%231E88E5" opacity="0.05"><rect x="10" y="10" width="20" height="20" rx="3"/></svg>');
          background-repeat: repeat;
        }

        .gallery-caption {
          padding: 1.5rem;
        }

        .gallery-caption h4 {
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--slate-grey, #4A5260);
          margin-bottom: 0.8rem;
        }

        .gallery-caption p {
          color: var(--slate-grey, #4A5260);
          font-size: 0.95rem;
          margin-bottom: 1rem;
        }

        .category-items {
          list-style: none;
        }

        .category-items li {
          padding: 0.2rem 0;
          color: var(--slate-grey, #4A5260);
          position: relative;
          padding-left: 1rem;
          font-size: 0.9rem;
        }

        .category-items li::before {
          content: '•';
          position: absolute;
          left: 0;
          color: var(--brand-purple, #7B3FE4);
          font-weight: bold;
        }

        .cta-section {
          padding: 80px 0;
          background: linear-gradient(135deg, var(--brand-purple, #7B3FE4) 0%, var(--deep-plum, #5A1FB3) 100%);
          text-align: center;
          color: white;
        }

        .cta-content h2 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .cta-content p {
          font-size: 1.2rem;
          margin-bottom: 2rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          opacity: 0.9;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-button {
          display: inline-block;
          padding: 15px 35px;
          background: white;
          color: var(--brand-purple, #7B3FE4);
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
        }

        .cta-button:hover {
          background: #f0f0f0;
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(255, 255, 255, 0.3);
        }

        .cta-button-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
        }

        .cta-button-secondary:hover {
          background: white;
          color: var(--brand-purple, #7B3FE4);
        }

        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .section-header h2 {
            font-size: 2.2rem;
          }

          .container {
            padding: 0 1rem;
          }

          .brand-story {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .applications-grid {
            grid-template-columns: 1fr;
          }

          .brand-name {
            font-size: 2.2rem;
          }

          .brand-tagline {
            font-size: 1.1rem;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .gallery-grid {
            grid-template-columns: 1fr;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-award"></i>
          </div>
          <h1 className="animate-fade-in">Our Brands</h1>
          <p className="hero-subtitle animate-fade-in">
            Trusted brands delivering quality, innovation, and excellence in every product we create
          </p>
        </div>
      </section>

      <section className="brand-showcase-section">
        <div className="container">
          <div className="brand-showcase animate-on-scroll">
            <div className="brand-header">
              <div className="brand-logo-placeholder">
                <i className="fas fa-star"></i>
              </div>
              <h2 className="brand-name">Amogh</h2>
              <p className="brand-tagline">A trusted accessory to every cook - aspiring or pro</p>
              <p className="brand-meaning">Meaning 'Unfailing' - Living up to its name</p>
            </div>
            
            <div className="brand-content">
              <div className="brand-story">
                <div className="brand-description">
                  <h3>The Amogh Story</h3>
                  <p>
                    Amogh meaning 'Unfailing' is a brand of utensils living up to its name. It is a trusted accessory to every cook aspiring or pro. In a lot of designs and varieties, we cater to household, restaurants, hotels, weddings, gatherings and anything at any time.
                  </p>
                  <p>
                    We keep coming with new designs, colors improving the efficiency and pleasure of cooking with each iteration. Our commitment to quality and innovation makes Amogh the preferred choice for cooking enthusiasts across all segments.
                  </p>
                  <ul className="brand-highlights">
                    <li>Unfailing quality and reliability</li>
                    <li>Extensive design and variety range</li>
                    <li>Suitable for all cooking levels</li>
                    <li>Continuous innovation and improvement</li>
                  </ul>
                </div>
                
                <div className="brand-image-placeholder">
                  <div className="brand-image">
                    <img src="/images/amogh.svg" alt="Amogh Brand Showcase" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                  <div className="image-caption">
                    <h4>Amogh Brand Logo</h4>
                    <p>Symbolizing unfailing quality and trust</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Brand Features</h2>
            <p>What makes Amogh the trusted choice for cooks everywhere</p>
          </div>
          <div className="features-grid">
            {brandFeatures.map((feature, index) => (
              <div key={index} className="feature-card animate-on-scroll">
                <div className="feature-icon">
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="applications-section">
        <div className="container">
          <div className="section-header">
            <h2>Applications & Markets</h2>
            <p>Amogh serves diverse markets with specialized solutions for every cooking need</p>
          </div>
          <div className="applications-grid">
            {applicationAreas.map((area, index) => (
              <div key={index} className="application-card animate-on-scroll">
                <div className="app-header">
                  <div className="app-icon">
                    <i className={area.icon}></i>
                  </div>
                  <h3>{area.title}</h3>
                </div>
                <p className="app-description">{area.description}</p>
                <ul className="app-features">
                  {area.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="product-gallery-section">
        <div className="container">
          <div className="section-header">
            <h2>Product Categories</h2>
            <p>Comprehensive range of cookware and utensils for every cooking requirement</p>
          </div>
          <div className="gallery-grid">
            {productCategories.map((category, index) => (
              <div key={index} className="gallery-item animate-on-scroll">
                <div className="gallery-image">
                  <div className="image-content">
                    <i className="fas fa-utensils"></i>
                    <div className="image-text">{category.title}</div>
                    <div className="image-subtitle">Product showcase</div>
                  </div>
                </div>
                <div className="gallery-caption">
                  <h4>{category.title}</h4>
                  <p>{category.description}</p>
                  <ul className="category-items">
                    {category.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Experience Amogh Quality</h2>
            <p>
              Discover the unfailing quality and innovative designs that make Amogh the trusted choice for cooks everywhere.
            </p>
            <div className="cta-buttons">
              <button className="cta-button" onClick={() => window.location.href = '/contact/enquiry-form'}>
                Get Quote
              </button>
              <button className="cta-button cta-button-secondary" onClick={() => window.location.href = '/businesses'}>
                Explore Products
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandsPage;